import { formatDistance, isToday, isYesterday } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import deleteBookmarkProject from '@cartwhl/common/api/deleteBookmarkProject';
import postBookmarkProject from '@cartwhl/common/api/postBookmarkProject';
import { getStudioUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Image from '@cartwhl/ui/atoms/Image';
import Text from '@cartwhl/ui/base/Text';
import ProjectCardUI from '@cartwhl/ui/feature/ProjectCard';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import BookmarkIcon from '@cartwhl/ui/icons/BookmarkIcon';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import InfoIcon from '@cartwhl/ui/icons/InfoIcon';
import PersonIcon from '@cartwhl/ui/icons/PersonIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import UncheckedIcon from '@cartwhl/ui/icons/UncheckedIcon';
import Button from '@cartwhl/ui/input/Button';

import { DeleteProjectModal } from 'components/ProjectManager';
import LegacyProjectModal from 'components/modals/LegacyProjectModal';
import { projectStore } from 'stores';
import {
  LEGACY_PROJECT_ID,
  makeProjectInstanceKey,
  selectProjectRange,
  setSelectedProject,
  toggleSelectedProject,
} from 'stores/projectStore';

import EditableProjectName from './EditableProjectName';

function formatDateForListView(date: string | Date, prefix: string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isToday(dateObj)) {
    return `${prefix} today`;
  }
  if (isYesterday(dateObj)) {
    return `${prefix} yesterday`;
  }
  const distance = formatDistance(dateObj, new Date(), {
    includeSeconds: false,
    addSuffix: true,
  });
  return `${prefix} ${distance}`;
}

export const ProjectCard = ({
  projectID,
  title,
  type,
  preview,
  lastUpdatedAt,
  createdAt,
  isBookmarked,
  allProjectsInOrder,
  sectionKey,
  viewMode = 'grid',
}: {
  projectID: string;
  title: string;
  type: 'pose' | 'animate';
  preview: string;
  lastUpdatedAt: string;
  createdAt?: string;
  isBookmarked?: boolean;
  allProjectsInOrder?: Array<{ projectID: string; sectionKey?: string }>;
  sectionKey?: string;
  viewMode?: 'grid' | 'list';
}) => {
  const [bookmarked, setBookmarked] = useState(isBookmarked || false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [showLegacyModal, setShowLegacyModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(preview);
  const [showCheckedDuringDeselect, setShowCheckedDuringDeselect] =
    useState(false);
  const prevSelectedRef = useRef(false);
  const isMobile = useMediaQuery('(max-width: 640px)');
  const {
    bookmarkedProjects,
    selectedInstances,
    selectedInstanceKeys,
    lastSelectedInstanceKey,
  } = useSnapshot(projectStore.state);
  const instanceKey = makeProjectInstanceKey(projectID, sectionKey);
  const isSelected = Boolean(selectedInstances[instanceKey]);

  // Delay switching to unchecked state when deselecting to match fade duration and avoid flickering
  useEffect(() => {
    if (prevSelectedRef.current && !isSelected) {
      setShowCheckedDuringDeselect(true);
      const timer = setTimeout(() => {
        setShowCheckedDuringDeselect(false);
      }, 150);
      return () => clearTimeout(timer);
    } else if (isSelected) {
      setShowCheckedDuringDeselect(false);
    }
    prevSelectedRef.current = isSelected;
  }, [isSelected]);

  // Sync local bookmarked state with the store's bookmarked projects list
  useEffect(() => {
    if (bookmarkedProjects.status === 'ready' && bookmarkedProjects.data) {
      const isInBookmarks = bookmarkedProjects.data.some(
        (project) => project.projectID === projectID,
      );
      setBookmarked(isInBookmarks);
    }
  }, [bookmarkedProjects, projectID]);

  // Update previewSrc when preview prop changes
  useEffect(() => {
    setPreviewSrc(preview);
  }, [preview]);

  const handleBookmarkToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (bookmarkLoading) return;

    setBookmarkLoading(true);
    try {
      if (bookmarked) {
        await deleteBookmarkProject(projectID);
        setBookmarked(false);
      } else {
        await postBookmarkProject(projectID);
        setBookmarked(true);
      }
      // Reload bookmarked projects list to update the Bookmarks section
      projectStore.loadBookmarkedProjects();
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    } finally {
      setBookmarkLoading(false);
    }
  };

  let href =
    type === 'pose'
      ? `${getStudioUrl()}/pose/${projectID}`
      : `${getStudioUrl()}/motion/${projectID}`;

  if (projectID === LEGACY_PROJECT_ID) {
    href = `${getStudioUrl()}/home`;
  }

  const handleClick = (e: React.MouseEvent) => {
    // Only navigate if not clicking on an interactive element
    const target = e.target as HTMLElement;
    if (
      target.closest('button') &&
      !target.closest('button[data-project-card-checkbox]')
    ) {
      return;
    }
    if (
      target.closest('input') ||
      target.closest('a') ||
      target.closest('[role="button"]')
    ) {
      return;
    }

    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const isMetaKey = isMac ? e.metaKey : e.ctrlKey;
    const isShiftKey = e.shiftKey;

    if (isShiftKey || isMetaKey) {
      e.preventDefault();
      e.stopPropagation();

      if (isShiftKey && allProjectsInOrder) {
        const anchorKey =
          lastSelectedInstanceKey ||
          selectedInstanceKeys[selectedInstanceKeys.length - 1] ||
          null;

        if (anchorKey) {
          selectProjectRange(
            anchorKey,
            projectID,
            allProjectsInOrder,
            sectionKey,
          );
        } else {
          setSelectedProject(projectID, sectionKey);
        }
      } else if (isMetaKey) {
        toggleSelectedProject(projectID, sectionKey);
      }
      return;
    }
    window.location.href = href;
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const isMetaKey = isMac ? e.metaKey : e.ctrlKey;
    const isShiftKey = e.shiftKey;

    if (isShiftKey && allProjectsInOrder) {
      const anchorKey =
        lastSelectedInstanceKey ||
        selectedInstanceKeys[selectedInstanceKeys.length - 1] ||
        null;

      if (anchorKey) {
        selectProjectRange(
          anchorKey,
          projectID,
          allProjectsInOrder,
          sectionKey,
        );
      } else {
        setSelectedProject(projectID, sectionKey);
      }
    } else if (isMetaKey) {
      toggleSelectedProject(projectID, sectionKey);
    } else {
      toggleSelectedProject(projectID, sectionKey);
    }
  };

  if (viewMode === 'list') {
    const createdDate = createdAt || lastUpdatedAt;
    const createdText = formatDateForListView(createdDate, 'Created');
    // Default to createdAt if lastUpdatedAt is missing or empty
    const editedDate =
      lastUpdatedAt && lastUpdatedAt.trim()
        ? lastUpdatedAt
        : createdAt || new Date().toISOString();
    const editedText = formatDateForListView(editedDate, 'Edited');

    return (
      <>
        <div
          data-project-card
          onClick={handleClick}
          className={`group relative flex cursor-pointer items-center gap-3 rounded-2xl p-3 transition-colors select-none ${
            isSelected
              ? 'bg-blue/10 dark:bg-blue/20'
              : 'hover:bg-gray-15 dark:hover:bg-gray-75'
          }`}
        >
          {/* Small thumbnail - 22.5% of horizontal space (50% wider than 15%), maintains 16:9 aspect ratio */}
          <div
            className={`relative aspect-video flex-shrink-0 overflow-hidden rounded-lg ${
              isMobile ? 'w-36' : 'w-[22.5%]'
            }`}
          >
            <button
              data-project-card-checkbox
              onClick={handleCheckboxClick}
              className={`absolute top-1 left-1 z-30 transition-opacity ${
                isSelected
                  ? 'opacity-100'
                  : isMobile
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100'
              }`}
            >
              {isSelected || showCheckedDuringDeselect ? (
                <div className="relative flex h-8 w-8 items-center justify-center">
                  <UncheckedIcon className="stroke-blue [&>rect]:fill-blue absolute inset-0 h-8 w-8 [&>rect]:[rx:64] [&>rect]:[ry:64]" />
                  <CheckmarkIcon className="relative h-5 w-5 text-white" />
                </div>
              ) : (
                <UncheckedIcon className="stroke-gray-45 dark:stroke-gray-15 dark:[&>rect]:fill-gray-75 h-8 w-8 [&>rect]:fill-white [&>rect]:[rx:64] [&>rect]:[ry:64]" />
              )}
            </button>
            <div className="hover:shadow-menu block h-full w-full select-none">
              {!previewSrc ? (
                <div className="bg-gray-30 dark:bg-gray-70 flex h-full w-full items-center justify-center">
                  <PersonIcon className="text-gray-70 dark:text-gray-45 h-8 w-8" />
                </div>
              ) : previewSrc.includes('thumbnail.mp4') ? (
                <video
                  src={previewSrc}
                  className="h-full w-full bg-gray-100 object-cover"
                  muted
                  loop
                  autoPlay
                  playsInline
                  crossOrigin="anonymous"
                  onError={() => setPreviewSrc('')}
                />
              ) : (
                <Image
                  src={previewSrc}
                  alt=""
                  crossOrigin="anonymous"
                  className="h-full w-full bg-gray-100 object-cover"
                  onError={() => setPreviewSrc('')}
                />
              )}
            </div>
            {/* Bookmark icon on thumbnail hover */}
            {projectID !== LEGACY_PROJECT_ID && (
              <Button
                variant="secondary"
                size="sm"
                icon={<BookmarkIcon className="h-4 w-4" fill={bookmarked} />}
                onClick={handleBookmarkToggle}
                disabled={bookmarkLoading}
                className={`absolute top-1 right-1 z-20 px-1 transition-opacity ${
                  bookmarked
                    ? 'opacity-100'
                    : isMobile
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                }`}
              />
            )}
          </div>

          {isMobile ? (
            // Mobile list view: stacked layout
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div
                className={`h-3 w-3 flex-shrink-0 rounded-full ${
                  type === 'pose' ? 'bg-violet' : 'bg-green'
                }`}
              />
              <div className="min-w-0">
                <EditableProjectName
                  projectID={projectID}
                  projectName={title}
                />
              </div>
              <Text
                variant="studio/body/md"
                className="text-secondary whitespace-nowrap"
              >
                {editedText}
              </Text>
            </div>
          ) : (
            // Desktop list view: horizontal layout
            <>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div
                  className={`h-3 w-3 flex-shrink-0 rounded-full ${
                    type === 'pose' ? 'bg-violet' : 'bg-green'
                  }`}
                />

                <div className="min-w-0 flex-1">
                  <EditableProjectName
                    projectID={projectID}
                    projectName={title}
                  />
                </div>
              </div>

              <div className="flex flex-1 items-center justify-start">
                <Text
                  variant="studio/body/md"
                  className="text-secondary whitespace-nowrap"
                >
                  {createdText}
                </Text>
              </div>

              <div className="flex flex-1 items-center justify-start">
                <Text
                  variant="studio/body/md"
                  className="text-secondary whitespace-nowrap"
                >
                  {editedText}
                </Text>
              </div>
            </>
          )}

          {projectID !== LEGACY_PROJECT_ID ? (
            <Dropdown
              trigger={
                <Button
                  variant="tertiary"
                  size="sm"
                  icon={<ThreeDotsIcon />}
                  className="flex-shrink-0"
                />
              }
            >
              <Dropdown.Item
                onClick={() => {
                  setIsDeleteModalOpen(true);
                }}
              >
                Delete
              </Dropdown.Item>
            </Dropdown>
          ) : (
            <Button
              variant="tertiary"
              size="sm"
              icon={<InfoIcon />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowLegacyModal(true);
              }}
            />
          )}
        </div>
        <DeleteProjectModal
          open={isDeleteModalOpen}
          onOpenChange={setIsDeleteModalOpen}
          onConfirm={() => projectStore.deleteProject(projectID)}
          projectCount={1}
        />
      </>
    );
  }

  return (
    <>
      <ProjectCardUI
        title={
          <EditableProjectName projectID={projectID} projectName={title} />
        }
        href={href}
        type={type}
        preview={previewSrc}
        lastUpdatedAt={
          lastUpdatedAt && lastUpdatedAt.trim()
            ? lastUpdatedAt
            : createdAt || new Date().toISOString()
        }
        onClick={handleClick}
        isSelected={isSelected}
        showCheckedDuringDeselect={showCheckedDuringDeselect}
        onCheckboxClick={handleCheckboxClick}
        isMobile={isMobile}
        overlays={
          <>
            {projectID !== LEGACY_PROJECT_ID && (
              <Button
                variant="secondary"
                size="sm"
                icon={<BookmarkIcon className="h-5 w-5" fill={bookmarked} />}
                onClick={handleBookmarkToggle}
                disabled={bookmarkLoading}
                className={`absolute top-2 right-2 z-20 px-1 transition-opacity ${
                  bookmarked
                    ? 'opacity-100'
                    : isMobile
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100'
                }`}
              />
            )}
            {projectID !== LEGACY_PROJECT_ID ? (
              <Dropdown
                trigger={
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<ThreeDotsIcon />}
                    className={`absolute top-2 right-12 z-20 transition-opacity ${
                      isMobile
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100'
                    }`}
                  />
                }
              >
                <Dropdown.Item
                  onClick={() => {
                    setIsDeleteModalOpen(true);
                  }}
                >
                  Delete
                </Dropdown.Item>
              </Dropdown>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                icon={<InfoIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLegacyModal(true);
                }}
                className={`absolute top-2 right-2 z-20 transition-opacity ${
                  isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
              />
            )}
          </>
        }
      />
      {projectID === LEGACY_PROJECT_ID && (
        <LegacyProjectModal
          open={showLegacyModal}
          onClose={() => setShowLegacyModal(false)}
        />
      )}
      <DeleteProjectModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={() => projectStore.deleteProject(projectID)}
        projectCount={1}
      />
    </>
  );
};
